import axios from "axios";
import { getToken } from "./authApi";
import {ApiResponse, BaseCreateResponse, BaseInfo, BaseInfoList, BaseResponse, baseUrl, deleteWithAuth, GetParams, getWithAuth, getWithAuthAndParams, Guid, ListRespone, patchWithAuth, postWithAuth} from "./baseApi";
import { BaseChannelModel, SiteNestedPlaylistForCampaign } from "./channelsApi";
import { Client, CreateClientRequest } from "./clientApi";
import { HiveStackRequest, MediaType, MediaVastRequest, MediaVastResponse } from "./mediaApi";


export type CampaingListModel = {
    id : string,
    name : string,
    campaignId: string | null,
    status: CampaignStatus,
    periods: CampaignPeriod[],
    client : Client,
    playLists: SiteNestedPlaylistForCampaign[]
    tags: BaseInfo[],
    isLockedFromDeleting: boolean,
    createdBy: string,
    createDate: string
}

export type CampaignPeriod = {
    startDate: string,
    endDate: string
}


export type GetCampaignResponse = {
    campaigns : CampaingListModel[]
}


export interface CreateCampaignRequest{
    name : string,
    campaignId: string | null,
    clientId: string,
    periods : CampaignPeriod[],
    playListIds: Guid[],
    tagIds: Guid[],
}

export interface UpdateCampaignRequest extends CreateCampaignRequest{
    id : Guid
}

export type CreateCampaignResponse = {
    id : string,
}

export type GetCampaignMediaResponse = {
    media : CampaignMediaItem[]
}

export type CampaignMediaItem = {
    id : string,
    mediaId : string,
    name : string,
    thumbnailUrl : string,
    mediaType : MediaType,
    size : number,
    durationMs : number,
    deleteDate: string,
    isPaused: boolean,
    schedule: CampaignMediaScheduleResponse[]
}

export type AddCampaignMediaRequest = {
    campaignId: string,
    mediaIds: string[]
}

export type AddCampaignMediaResponse = {
}

export enum CampaignStatus{
    Draft = 1,
    Scheduled = 2,
    Active = 3,
    Expired = 4,
    Paused = 5,
}

export type StopPlayRequest = {
    campaignId : Guid
}

export type ChangeStatusCampaignMediaRequest = {
    campaignMediaId: Guid
}

export interface CampaignMediaScheduleRequest {
    name: string,
    start: string,
    end: string,
    startTime: string,
    endTime: string
}

export type SwapCampaignMediaRequest = {
    campaignId:Guid,
    mediaIdToSwap: Guid,
    mediaId: Guid
}

export interface CampaignMediaScheduleResponse extends CampaignMediaScheduleRequest {
    id: Guid
}

export const getCampaigns = async() : Promise<ApiResponse<GetCampaignResponse>> => {
    return await getWithAuth<GetCampaignResponse>({url : `${baseUrl}campaign`});
}

export const getCampaignsBase = async() : Promise<ApiResponse<BaseInfoList>> => {
    return await getWithAuth<BaseInfoList>({url : `${baseUrl}campaign/base`});
}

export const getCampaignsArchived = async() : Promise<ApiResponse<GetCampaignResponse>> => {
    return await getWithAuth<GetCampaignResponse>({url : `${baseUrl}campaign/archived`});
}

export const createCampaign = async(request : CreateCampaignRequest) : Promise<ApiResponse<CreateCampaignResponse>> => {
    return await postWithAuth<CreateClientRequest, CreateCampaignResponse>(`${baseUrl}campaign`, request);
}

export const updateCampaign = async(request : UpdateCampaignRequest) : Promise<ApiResponse<BaseResponse>> => {
    return await patchWithAuth<UpdateCampaignRequest, BaseResponse>(`${baseUrl}campaign`, request);
}

export const getCampaignMedia = async(id : string) : Promise<ApiResponse<GetCampaignMediaResponse>> => {
    var params = new GetParams();
    params.Add("id", id);
    return await getWithAuthAndParams<GetCampaignMediaResponse>(`${baseUrl}campaign/media`, params);
}

export const addCampaignMedia = async(request : AddCampaignMediaRequest) : Promise<ApiResponse<AddCampaignMediaResponse>> => {
    return await postWithAuth<AddCampaignMediaRequest, AddCampaignMediaResponse>(`${baseUrl}campaign/media`, request);
}

export const pauseCampaign = async(request : StopPlayRequest) : Promise<ApiResponse<BaseResponse>> => {
    return await postWithAuth<StopPlayRequest, BaseResponse>(`${baseUrl}campaign/pause`, request);
}

export const publishCampaign = async(request : StopPlayRequest) : Promise<ApiResponse<BaseResponse>> => {
    return await postWithAuth<StopPlayRequest, BaseResponse>(`${baseUrl}campaign/publish`, request);
}

export const deleteCampaign = async(id: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await deleteWithAuth<BaseResponse>(`${baseUrl}campaign`, id);
}

export const deleteCampaignMedia = async(id: Guid) : Promise<ApiResponse<BaseResponse>> => {
    return await deleteWithAuth<BaseResponse>(`${baseUrl}campaign/media`, id);
}

export const pauseCampaignMedia = async(request: ChangeStatusCampaignMediaRequest) : Promise<ApiResponse<BaseResponse>> => {
    return await postWithAuth<ChangeStatusCampaignMediaRequest, BaseResponse>(`${baseUrl}campaign/media/pause`, request);
}

export const playCampaignMedia = async(request: ChangeStatusCampaignMediaRequest) : Promise<ApiResponse<BaseResponse>> => {
    return await postWithAuth<ChangeStatusCampaignMediaRequest, BaseResponse>(`${baseUrl}campaign/media/play`, request);
}

export const uploadMediaForCampaign = async (formData: any, camapignId: Guid, setCurrentProgress?:(progress: number)=>void) : Promise<"ok" | "error"> => {
   try {
        await axios.post(`${baseUrl}campaign/${camapignId}/media/upload`, formData, {
            headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization' : getToken()
            }, 
            onUploadProgress: (progressEvent:any) => {
                let progress = Math.round(
                    (progressEvent.loaded / progressEvent.total) * 100
                );
                setCurrentProgress && setCurrentProgress(progress);
            }
        });
   } catch (error) {
        setCurrentProgress && setCurrentProgress(0);
   }

    return "ok"
}

export const postMediaCampaignVast = async (request: MediaVastRequest, camapignId: Guid) :Promise<ApiResponse<MediaVastResponse>> => {
    return await postWithAuth<MediaVastRequest, MediaVastResponse>(`${baseUrl}campaign/${camapignId}/media/vasturl`, request);
}

export const getCampaignMediaSchedule = async (id: Guid, mediaId: Guid): Promise<ApiResponse<ListRespone<CampaignMediaScheduleResponse>>> => {
    return await getWithAuth<ListRespone<CampaignMediaScheduleResponse>>({url : `${baseUrl}campaign/${id}/media/${mediaId}/schedule`});
}

export const postCampaignMediaSchedulePeriod = async (request: CampaignMediaScheduleRequest, id:Guid,  mediaId: Guid): Promise<ApiResponse<BaseCreateResponse>> => {
    return await postWithAuth<CampaignMediaScheduleRequest,BaseCreateResponse>(`${baseUrl}campaign/${id}/media/${mediaId}/schedule`, request);
}

export const deleteCampaignMediaSchedulePeriod = async (id: Guid, mediaId:Guid, scheduleId:Guid):Promise<ApiResponse<BaseResponse>> => {
    return await deleteWithAuth<BaseResponse>(`${baseUrl}campaign/${id}/media/${mediaId}/schedule`, scheduleId);
}

export const postCampaignLock = async (id:Guid): Promise<ApiResponse<BaseCreateResponse>> => {
    return await postWithAuth<{},BaseCreateResponse>(`${baseUrl}campaign/${id}/lock-deleting`, {});
}

export const postCampaignUnlock = async (id:Guid): Promise<ApiResponse<BaseCreateResponse>> => {
    return await postWithAuth<{},BaseCreateResponse>(`${baseUrl}campaign/${id}/unlock-deleting`, {});
}

export const postCampaignHiveStack = async (request: HiveStackRequest, camapignId: Guid) :Promise<ApiResponse<BaseResponse>> => {
    return await postWithAuth<HiveStackRequest, BaseResponse>(`${baseUrl}campaign/${camapignId}/media/hivestack`, request);
}

export const swapCampaignMedia = async(request : SwapCampaignMediaRequest) : Promise<ApiResponse<{}>> => {
    return await postWithAuth<SwapCampaignMediaRequest, {}>(`${baseUrl}campaign/media/swap`, request);
}
